import type { IdealCaptureSize, TexelDensityResult } from './types';
import { triArea } from './triangle';

// Area-weighted percentile of per-triangle texel density used to size the capture.
// 0.9 "leans toward max" — resolves ~90% of the surface area at full source detail,
// while the 10% headroom + area weighting skip degenerate-UV slivers (tiny area,
// extreme density) that a true max would let blow up the resolution.
const DENSITY_PERCENTILE = 0.9;

/**
 * Estimate the source texture's texel density over the region — how many texture
 * texels map to one world unit. This is the fidelity ceiling: capturing beyond it
 * just upsamples the GLB texture. Per triangle we compare its UV-space area (×
 * texture dims) to its world area; the region figure is the AREA-WEIGHTED 90th
 * PERCENTILE (captures the genuinely sharp areas; robust to atlas seams /
 * degenerate-UV slivers). Multi-material regions are handled per-triangle.
 * `coverage` is the fraction of region area with usable UVs.
 */
export function computeTexelDensity(
  positions: Float32Array,
  uvs: Float32Array,
  indices: Uint32Array,
  regionTris: Uint32Array,
  triMeshIndex: Uint32Array,
  meshes: { textureSize: { width: number; height: number } | null }[],
): TexelDensityResult {
  const samples: { density: number; area: number }[] = [];
  let totalArea = 0;
  let coveredArea = 0;

  for (let i = 0; i < regionTris.length; i++) {
    const t = regionTris[i];
    const worldArea = triArea(positions, indices, t);
    totalArea += worldArea;
    if (worldArea < 1e-12) continue;

    const tex = meshes[triMeshIndex[t]]?.textureSize;
    if (!tex) continue;

    const i0 = indices[t * 3], i1 = indices[t * 3 + 1], i2 = indices[t * 3 + 2];
    const u0 = uvs[i0 * 2], v0 = uvs[i0 * 2 + 1];
    const u1 = uvs[i1 * 2], v1 = uvs[i1 * 2 + 1];
    const u2 = uvs[i2 * 2], v2 = uvs[i2 * 2 + 1];
    if (![u0, v0, u1, v1, u2, v2].every(Number.isFinite)) continue;

    const cross2 = (u1 - u0) * (v2 - v0) - (v1 - v0) * (u2 - u0);
    const texelArea = 0.5 * Math.abs(cross2) * tex.width * tex.height;
    const density = Math.sqrt(texelArea / worldArea);
    samples.push({ density, area: worldArea });
    coveredArea += worldArea;
  }

  if (samples.length === 0 || coveredArea < 1e-12) {
    return { texelsPerUnit: 0, coverage: 0 };
  }

  samples.sort((x, y) => x.density - y.density);
  const threshold = DENSITY_PERCENTILE * coveredArea;
  let cumulative = 0;
  let picked = samples[samples.length - 1].density;
  for (const s of samples) {
    cumulative += s.area;
    if (cumulative >= threshold) {
      picked = s.density;
      break;
    }
  }

  return { texelsPerUnit: picked, coverage: totalArea > 0 ? coveredArea / totalArea : 0 };
}

/**
 * Ideal capture pixel size for a rect of world size (rectW × rectH): the output
 * matches the source texel density 1:1 (no upsampling, no detail lost). Aspect
 * ratio is preserved under all clamps. If the region lacks usable UVs
 * (`coverage < 0.5`) we fall back to a fixed 2048-px long edge and flag it.
 */
export function computeIdealCaptureSize(
  rectW: number,
  rectH: number,
  density: TexelDensityResult,
  opts: { maxDim?: number; minDim?: number } = {},
): IdealCaptureSize {
  const maxDim = opts.maxDim ?? 8192;
  const minDim = opts.minDim ?? 256;
  const aspect = rectH > 1e-9 ? rectW / rectH : 1;

  if (density.coverage < 0.5 || density.texelsPerUnit <= 0) {
    const FALLBACK_LONG = 2048;
    let width: number, height: number;
    if (aspect >= 1) {
      width = FALLBACK_LONG;
      height = Math.max(1, Math.round(FALLBACK_LONG / aspect));
    } else {
      height = FALLBACK_LONG;
      width = Math.max(1, Math.round(FALLBACK_LONG * aspect));
    }
    return { width, height, clamped: false, fallback: true };
  }

  let width = Math.max(1, Math.ceil(rectW * density.texelsPerUnit));
  let height = Math.max(1, Math.ceil(rectH * density.texelsPerUnit));

  const longest = Math.max(width, height);
  let factor = 1;
  let clamped = false;
  if (longest > maxDim) {
    factor = maxDim / longest;
    clamped = true;
  } else if (longest < minDim) {
    factor = minDim / longest;
    clamped = true;
  }
  if (factor !== 1) {
    width = Math.max(1, Math.round(width * factor));
    height = Math.max(1, Math.round(height * factor));
  }

  return { width, height, clamped, fallback: false };
}
